// ============================================
// moldClaw - Payment Success Page (Polar)
// ============================================

/**
 * 🧑‍💻 SWE-2: 결제 성공 페이지
 * 🎨 CX-2: 토스 결제 성공 UX 벤치마킹
 *   - 깔끔한 성공 메시지
 *   - 다음 단계 안내
 *   - 영수증 확인 링크
 * 🔧 POLAR: Polar Checkout 성공 처리 (2026-03-16)
 *   - checkout_id로 결제 상태 확인
 *   - 구독 활성화 폴링
 */

import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { usePayment } from '../hooks/usePayment';
import { TIER_INFO } from '../hooks/useLicense';
import { PolarCheckoutStatus } from '../lib/api';

// ============================================
// Security: URL 파라미터 검증
// ============================================

/**
 * checkout_id 형식 검증 (Polar checkout ID)
 * Polar uses UUID format or similar alphanumeric IDs
 */
function isValidCheckoutId(id: string | null): id is string {
  if (!id) return false;
  // UUID 형식 또는 알파벳+숫자+하이픈 조합 (32~64자)
  // Polar checkout ID는 보통 UUID 형식
  const uuidRegex = /^[a-zA-Z0-9-]{8,64}$/;
  return uuidRegex.test(id);
}

/**
 * tier 파라미터 검증
 */
function isValidTier(tier: string | null): tier is 'pro' | 'team' {
  return tier === 'pro' || tier === 'team';
}

// 플랜 정보 (TIER_INFO 기반으로 일관성 유지)
const PLAN_INFO = {
  pro: { 
    name: TIER_INFO.pro.name, 
    price: TIER_INFO.pro.priceLabel, 
    features: TIER_INFO.pro.features.slice(0, 3)
  },
  team: { 
    name: TIER_INFO.team.name, 
    price: TIER_INFO.team.priceLabel, 
    features: TIER_INFO.team.features.slice(0, 3)
  },
};

type VerificationState = 'verifying' | 'confirmed' | 'pending' | 'failed';

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { verifyCheckout, loadSubscription, subscription, isLoading, error } = usePayment();
  
  const [showConfetti, setShowConfetti] = useState(false);
  const [verificationState, setVerificationState] = useState<VerificationState>('verifying');
  const [checkoutStatus, setCheckoutStatus] = useState<PolarCheckoutStatus | null>(null);
  const [pollCount, setPollCount] = useState(0);
  
  // URL 파라미터에서 checkout_id 추출 및 검증
  const rawCheckoutId = searchParams.get('checkout_id');
  const rawTierParam = searchParams.get('tier');
  
  // Security: 파라미터 검증
  const checkoutId = isValidCheckoutId(rawCheckoutId) ? rawCheckoutId : null;
  const tierParam = isValidTier(rawTierParam) ? rawTierParam : null;
  
  // 유효하지 않은 파라미터 감지 시 로깅 (공격 시도 탐지용)
  useEffect(() => {
    if (rawCheckoutId && !checkoutId) {
      console.warn('[Security] Invalid checkout_id format detected');
    }
  }, [rawCheckoutId, checkoutId]);
  
  // Checkout 상태 확인
  const verifyPayment = useCallback(async () => {
    if (!checkoutId) {
      setVerificationState('failed');
      return;
    }
    
    try {
      const status = await verifyCheckout(checkoutId);
      setCheckoutStatus(status);
      
      if (status.status === 'confirmed') {
        setVerificationState('confirmed');
        setShowConfetti(true);
        // 구독 상태 새로고침
        await loadSubscription();
      } else if (status.status === 'open') {
        // 아직 처리 중 - webhook 대기
        setVerificationState('pending');
      } else if (status.status === 'failed' || status.status === 'expired') {
        setVerificationState('failed');
      }
    } catch {
      // 에러 발생 시 pending으로 처리 (webhook이 처리할 수 있음)
      setVerificationState('pending');
    }
  }, [checkoutId, verifyCheckout, loadSubscription]);
  
  // 초기 확인 및 폴링
  useEffect(() => {
    verifyPayment();
  }, [verifyPayment]);
  
  // Pending 상태일 때 폴링 (최대 10회, 3초 간격)
  useEffect(() => {
    if (verificationState === 'pending' && pollCount < 10) {
      const timer = setTimeout(() => {
        setPollCount(prev => prev + 1);
        verifyPayment();
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [verificationState, pollCount, verifyPayment]);
  
  // Confetti 3초 후 숨기기
  useEffect(() => {
    if (showConfetti) {
      const timer = setTimeout(() => setShowConfetti(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [showConfetti]);
  
  // 티어 정보 결정
  const tier = subscription?.tier || tierParam || 'pro';
  const planInfo = PLAN_INFO[tier as 'pro' | 'team'] || PLAN_INFO.pro;
  const amount = checkoutStatus?.amount 
    ? `₩${checkoutStatus.amount.toLocaleString()}` 
    : planInfo.price;
  
  // 로딩 상태
  if (verificationState === 'verifying' || isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-6">
            <svg className="animate-spin w-full h-full text-space-blue" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" 
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">결제 확인 중...</h2>
          <p className="text-gray-400">잠시만 기다려주세요</p>
        </div>
      </div>
    );
  }
  
  // Pending 상태 (webhook 대기 중)
  if (verificationState === 'pending') {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="bg-gray-800 rounded-2xl p-8 text-center shadow-xl">
            <div className="w-20 h-20 mx-auto mb-6 bg-yellow-500/20 rounded-full flex items-center justify-center">
              <svg className="w-10 h-10 text-yellow-400 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            
            <h1 className="text-2xl font-bold text-white mb-2">
              결제 처리 중입니다
            </h1>
            
            <p className="text-gray-400 mb-6">
              결제가 확인되면 자동으로 구독이 활성화됩니다.<br/>
              보통 1-2분 내에 완료됩니다.
            </p>
            
            <div className="bg-gray-700/50 rounded-xl p-4 mb-6">
              <div className="flex items-center justify-center gap-2 text-sm text-gray-300">
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" 
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span>확인 중... ({pollCount}/10)</span>
              </div>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full py-3 px-4 bg-space-blue hover:brightness-110 text-white rounded-lg font-medium transition-colors"
              >
                대시보드에서 확인하기
              </button>
              
              <p className="text-xs text-gray-500">
                문제가 계속되면{' '}
                <a href="mailto:support@moldclaw.com" className="text-space-blue-light hover:underline">
                  support@moldclaw.com
                </a>
                으로 문의해주세요
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // 실패 상태
  if (verificationState === 'failed' || error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="bg-gray-800 rounded-2xl p-8 text-center shadow-xl">
            <div className="w-20 h-20 mx-auto mb-6 bg-red-500/20 rounded-full flex items-center justify-center">
              <svg className="w-10 h-10 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            
            <h1 className="text-2xl font-bold text-white mb-2">
              결제 확인에 실패했습니다
            </h1>
            
            <p className="text-gray-400 mb-6">
              {error || '결제 상태를 확인할 수 없습니다. 잠시 후 다시 시도해주세요.'}
            </p>
            
            <div className="space-y-3">
              <button
                onClick={() => verifyPayment()}
                className="w-full py-3 px-4 bg-space-blue hover:brightness-110 text-white rounded-lg font-medium transition-colors"
              >
                다시 확인하기
              </button>
              
              <button
                onClick={() => navigate('/pricing')}
                className="w-full py-3 px-4 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
              >
                요금제 페이지로 돌아가기
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // 성공 상태
  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
      {/* Confetti Animation */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-confetti"
              style={{
                left: `${Math.random() * 100}%`,
                top: '-10px',
                animationDelay: `${Math.random() * 2}s`,
                backgroundColor: ['#6366f1', '#8b5cf6', '#ec4899', '#10b981', '#f59e0b'][i % 5],
                width: '10px',
                height: '10px',
                borderRadius: Math.random() > 0.5 ? '50%' : '0',
              }}
            />
          ))}
        </div>
      )}
      
      <div className="max-w-md w-full">
        {/* Success Card */}
        <div className="bg-gray-800 rounded-2xl p-8 text-center shadow-xl">
          {/* Success Icon */}
          <div className="w-20 h-20 mx-auto mb-6 bg-green-500/20 rounded-full flex items-center justify-center">
            <svg className="w-10 h-10 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          
          {/* Title */}
          <h1 className="text-2xl font-bold text-white mb-2">
            결제가 완료되었습니다! 🎉
          </h1>
          
          <p className="text-gray-400 mb-6">
            {planInfo.name} 플랜으로 업그레이드되었습니다
          </p>
          
          {/* Plan Details */}
          <div className="bg-gray-700/50 rounded-xl p-4 mb-6 text-left">
            <div className="flex justify-between items-center mb-3">
              <span className="text-gray-400">플랜</span>
              <span className="text-white font-semibold">{planInfo.name}</span>
            </div>
            <div className="flex justify-between items-center mb-3">
              <span className="text-gray-400">결제 금액</span>
              <span className="text-white font-semibold">{amount}/월</span>
            </div>
            {subscription && (
              <div className="flex justify-between items-center">
                <span className="text-gray-400">다음 결제일</span>
                <span className="text-white">
                  {new Date(subscription.current_period_end).toLocaleDateString('ko-KR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </span>
              </div>
            )}
          </div>
          
          {/* Features Unlocked */}
          <div className="mb-6 text-left">
            <p className="text-sm text-gray-400 mb-2">사용 가능한 기능:</p>
            <ul className="space-y-1">
              {planInfo.features.map((feature, i) => (
                <li key={i} className="flex items-center text-sm text-green-400">
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {feature}
                </li>
              ))}
            </ul>
          </div>
          
          {/* Actions */}
          <div className="space-y-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full py-3 px-4 bg-space-blue hover:brightness-110 text-white rounded-lg font-medium transition-colors"
            >
              대시보드로 이동
            </button>
            
            <Link
              to="/download"
              className="block w-full py-3 px-4 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
            >
              앱 다운로드
            </Link>
          </div>
        </div>
        
        {/* Help & Security Info */}
        <div className="mt-6 text-center space-y-2">
          <p className="text-gray-500 text-sm">
            결제 관련 문의:{' '}
            <a href="mailto:support@moldclaw.com" className="text-space-blue-light hover:text-white transition-colors">
              support@moldclaw.com
            </a>
          </p>
          {user && (
            <p className="text-gray-600 text-xs">
              결제 확인 이메일이 {user.email}로 발송됩니다
            </p>
          )}
          <div className="flex items-center justify-center gap-4 text-xs text-gray-600">
            <span className="flex items-center gap-1">
              <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              SSL 암호화
            </span>
            <span>•</span>
            <Link to="/terms#refund" className="hover:text-gray-400 transition-colors">
              환불 정책
            </Link>
          </div>
        </div>
      </div>
      
      {/* Confetti CSS */}
      <style>{`
        @keyframes confetti-fall {
          0% {
            transform: translateY(-10px) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
        .animate-confetti {
          animation: confetti-fall 3s ease-out forwards;
        }
      `}</style>
    </div>
  );
}
